/**
 * Единый справочник категорий FreelanCYX.
 * Используется ботом (регистрация), бэкендом (подписи / алерты) и Mini App.
 *
 * Ключи стабильны: менять label можно, переименовывать key — только с миграцией данных.
 */

/** @typedef {{ key: string, label: string }} SubCategory */
/** @typedef {{ key: string, label: string, orderKey?: string, sub: SubCategory[] }} MainCategory */

/**
 * orderKey — ключ в orders.category / алертах, если отличается от профиля
 * (исторически заказы используют `text`, профиль — `texts`).
 *
 * @type {MainCategory[]}
 */
const FREELANCE_MAIN_CATEGORIES = [
  {
    key: 'design',
    label: 'Дизайн',
    sub: [
      { key: 'logos', label: 'Логотипы' },
      { key: 'branding', label: 'Брендинг / фирстиль' },
      { key: 'uiux', label: 'UI/UX' },
      { key: 'banners', label: 'Баннеры и реклама' },
      { key: 'social_design', label: 'Дизайн для соцсетей' },
      { key: 'presentations', label: 'Презентации' },
      { key: 'icons_illustrations', label: 'Иконки и иллюстрации' },
      { key: 'print', label: 'Полиграфия / печать' },
      { key: 'packaging', label: 'Упаковка' },
      { key: 'merch', label: 'Мерч и сувениры' }
    ]
  },
  {
    key: 'web',
    label: 'Веб‑разработка',
    sub: [
      { key: 'landing', label: 'Лендинги' },
      { key: 'websites', label: 'Сайты под ключ' },
      { key: 'frontend', label: 'Верстка / Frontend' },
      { key: 'backend_api', label: 'Backend / API' },
      { key: 'integrations', label: 'Интеграции' },
      { key: 'wordpress', label: 'WordPress' },
      { key: 'ecommerce', label: 'Интернет‑магазины' },
      { key: 'tilda_constructors', label: 'Tilda / конструкторы' },
      { key: 'shopify', label: 'Shopify / CMS' },
      { key: 'support_web', label: 'Поддержка и доработки' }
    ]
  },
  {
    key: 'bots',
    label: 'Боты и автоматизация',
    sub: [
      { key: 'telegram_bots', label: 'Telegram‑боты' },
      { key: 'crypto_bots', label: 'Crypto‑боты и скрипты' },
      { key: 'discord_bots', label: 'Discord / другие мессенджеры' },
      { key: 'api_tools', label: 'API и тулзы' },
      { key: 'webhooks', label: 'Вебхуки' },
      { key: 'parsers', label: 'Парсеры / скрейпинг' },
      { key: 'schedulers', label: 'Планировщики / очереди' },
      { key: 'chatbots_other', label: 'Чат‑боты (не Telegram)' }
    ]
  },
  {
    key: 'mobile',
    label: 'Мобильные приложения',
    sub: [
      { key: 'android', label: 'Android' },
      { key: 'ios', label: 'iOS' },
      { key: 'crossplatform', label: 'Кроссплатформа' },
      { key: 'flutter_reactnative', label: 'Flutter / React Native' },
      { key: 'apps_ui', label: 'UI для приложений' },
      { key: 'app_store', label: 'Публикация в сторах' },
      { key: 'updates', label: 'Доработки и обновления' }
    ]
  },
  {
    key: 'scripts_ai',
    label: 'Скрипты и AI',
    sub: [
      { key: 'python_scripts', label: 'Python / скрипты' },
      { key: 'data_analysis', label: 'Аналитика данных' },
      { key: 'chatgpt_prompts', label: 'Промпты / агенты' },
      { key: 'automation', label: 'Автоматизация процессов' },
      { key: 'reports', label: 'Отчёты / дашборды' },
      { key: 'ml_models', label: 'ML / модели' },
      { key: 'n8n_zapier', label: 'n8n / Zapier / Make' },
      { key: 'rag_bots', label: 'RAG / базы знаний' }
    ]
  },
  {
    key: 'texts',
    label: 'Тексты и контент',
    orderKey: 'text',
    sub: [
      { key: 'copywriting', label: 'Копирайтинг' },
      { key: 'seo_texts', label: 'SEO‑тексты' },
      { key: 'naming', label: 'Нейминг' },
      { key: 'scripts', label: 'Сценарии' },
      { key: 'rewriting', label: 'Рерайт / редактура' },
      { key: 'articles', label: 'Статьи / лонгриды' },
      { key: 'product_cards', label: 'Карточки товаров' },
      { key: 'technical', label: 'Технические тексты' },
      { key: 'ghostwriting', label: 'Гострайтинг' },
      /** legacy: раньше переводы жили внутри texts */
      { key: 'translations', label: 'Переводы EN↔RU' }
    ]
  },
  {
    key: 'translation',
    label: 'Переводы',
    sub: [
      { key: 'en_ru', label: 'EN ↔ RU' },
      { key: 'de_ru', label: 'DE ↔ RU' },
      { key: 'es_ru', label: 'ES ↔ RU' },
      { key: 'zh_ru', label: 'ZH ↔ RU' },
      { key: 'other_pair', label: 'Другая языковая пара' },
      { key: 'localization', label: 'Локализация продуктов' },
      { key: 'technical_trans', label: 'Технический перевод' },
      { key: 'literary', label: 'Художественный перевод' },
      { key: 'editing_proof', label: 'Редактура перевода' },
      { key: 'transcription', label: 'Транскрипция / субтитры' }
    ]
  },
  {
    key: 'marketing',
    label: 'Маркетинг и продвижение',
    sub: [
      { key: 'seo', label: 'SEO' },
      { key: 'smm', label: 'SMM' },
      { key: 'target_ads', label: 'Таргет' },
      { key: 'context_ads', label: 'Контекстная реклама' },
      { key: 'content_plan', label: 'Контент‑план' },
      { key: 'email_mailing', label: 'Email‑рассылки' },
      { key: 'influencers', label: 'Инфлюенсеры / посевы' },
      { key: 'analytics', label: 'Аналитика и метрики' },
      { key: 'brand_strategy', label: 'Бренд‑стратегия' }
    ]
  },
  {
    key: 'video_audio',
    label: 'Видео и аудио',
    sub: [
      { key: 'video_editing', label: 'Монтаж видео' },
      { key: 'shorts', label: 'Shorts / Reels / TikTok' },
      { key: 'voiceover', label: 'Озвучка / диктор' },
      { key: 'subtitles', label: 'Субтитры' },
      { key: 'podcasts', label: 'Подкасты' },
      { key: 'animation_2d', label: '2D‑анимация' },
      { key: 'color_grading', label: 'Цветокор' },
      { key: 'intro_outro', label: 'Интро / аутро / заставки' }
    ]
  },
  {
    key: 'business',
    label: 'Бизнес и финансы',
    sub: [
      { key: 'consulting', label: 'Консалтинг' },
      { key: 'business_plans', label: 'Бизнес‑планы' },
      { key: 'market_research', label: 'Исследования рынка' },
      { key: 'accounting', label: 'Бухгалтерия / учёт' },
      { key: 'investments', label: 'Инвестиции / финмодели' },
      { key: 'legal_docs', label: 'Юридические документы' },
      { key: 'hr', label: 'HR / подбор' },
      { key: 'presentations_biz', label: 'Питчи / презентации' },
      { key: 'ops_process', label: 'Процессы и операции' },
      { key: 'fundraising', label: 'Фандрайзинг / гранты' }
    ]
  },
  {
    key: '3d_animation',
    label: '3D и анимация',
    sub: [
      { key: '3d_models', label: '3D‑модели' },
      { key: 'motion_design', label: 'Motion design' },
      { key: 'visualization', label: 'Визуализация' },
      { key: 'rendering', label: 'Рендер / арт' },
      { key: 'architectural', label: 'Архвиз' },
      { key: 'character', label: 'Персонажи' },
      { key: 'product_viz', label: 'Продуктовая визуализация' }
    ]
  },
  {
    key: 'music',
    label: 'Музыка и саунд',
    sub: [
      { key: 'composition', label: 'Композиции' },
      { key: 'sound_design', label: 'Sound design' },
      { key: 'remixes', label: 'Ремиксы' },
      { key: 'jingles', label: 'Джинглы' },
      { key: 'mixing', label: 'Сведение / мастеринг' },
      { key: 'beats', label: 'Биты / инструменталы' },
      { key: 'podcast_audio', label: 'Обработка подкастов' }
    ]
  },
  {
    key: 'other',
    label: 'Прочее',
    sub: [{ key: 'custom', label: 'Своя тема' }]
  }
];

/** Синонимы старых ключей → канонический key профиля */
const MAIN_ALIASES = {
  text: 'texts',
  /** legacy: иногда боты клали в scripts_ai в алертах */
  texts: 'texts',
  /** legacy: старые заказы с category=dev */
  dev: 'web'
};

function getMainByKey(mainKey) {
  const raw = String(mainKey || '').trim();
  if (!raw) return null;
  const aliased = MAIN_ALIASES[raw] || raw;
  return FREELANCE_MAIN_CATEGORIES.find((c) => c.key === aliased || c.orderKey === raw) || null;
}

function getSubLabel(mainKey, subKey) {
  const main = getMainByKey(mainKey);
  if (!main) return subKey;
  const s = (main.sub || []).find((x) => x.key === subKey);
  return s?.label || subKey;
}

function orderKeyForMain(main) {
  if (!main) return null;
  return main.orderKey || main.key;
}

/**
 * Карта подписей основных категорий (включая alias text ↔ texts).
 * @returns {Record<string, string>}
 */
function buildMainCategoryLabels() {
  /** @type {Record<string, string>} */
  const out = {};
  for (const m of FREELANCE_MAIN_CATEGORIES) {
    out[m.key] = m.label;
    if (m.orderKey) out[m.orderKey] = m.label;
  }
  // Совместимость со старыми подписями заказов
  if (out.texts && !out.text) out.text = out.texts;
  return out;
}

/**
 * @returns {Record<string, Record<string, string>>}
 */
function buildSubcategoryLabels() {
  /** @type {Record<string, Record<string, string>>} */
  const out = {};
  for (const m of FREELANCE_MAIN_CATEGORIES) {
    /** @type {Record<string, string>} */
    const map = {};
    for (const s of m.sub || []) map[s.key] = s.label;
    out[m.key] = map;
    if (m.orderKey) out[m.orderKey] = map;
  }
  return out;
}

/**
 * Опции для select заказов / фильтров / алертов (ключ = orderKey).
 * @returns {Array<{ value: string, label: string, profileKey: string }>}
 */
function buildOrderCategoryOptions() {
  return FREELANCE_MAIN_CATEGORIES.map((m) => ({
    value: orderKeyForMain(m),
    label: m.label,
    profileKey: m.key
  }));
}

/**
 * Опции алертов: value = order key, subSource = ключ для SUBCATEGORY_LABELS.
 * @returns {Array<{ value: string, label: string, subSource: string }>}
 */
function buildAlertOrderCategoryOptions() {
  return FREELANCE_MAIN_CATEGORIES.map((m) => ({
    value: orderKeyForMain(m),
    label: m.label,
    subSource: m.key
  }));
}

/**
 * Канонический ключ раздела для алертов / заказов (orderKey).
 */
function canonicalizeOrderMain(raw) {
  const t = String(raw || '').trim();
  if (!t) return null;
  const main = getMainByKey(t);
  if (!main) return null;
  return orderKeyForMain(main);
}

/**
 * Ключи/префиксы category для полнотекстового поиска по русским подписям.
 * @param {string} q
 * @returns {{ exact: string[], prefixes: string[] }}
 */
function categoryMatchersForSearch(q) {
  const needle = String(q || '')
    .trim()
    .toLowerCase();
  /** @type {Set<string>} */
  const exact = new Set();
  /** @type {Set<string>} */
  const prefixes = new Set();
  if (needle.length < 2) return { exact: [], prefixes: [] };

  for (const m of FREELANCE_MAIN_CATEGORIES) {
    const ok = orderKeyForMain(m);
    const mainHit =
      String(m.label || '')
        .toLowerCase()
        .includes(needle) ||
      String(m.key).toLowerCase().includes(needle) ||
      String(m.orderKey || '')
        .toLowerCase()
        .includes(needle);
    if (mainHit) {
      exact.add(ok);
      exact.add(m.key);
      prefixes.add(`sub:${ok}:`);
      prefixes.add(`sub:${m.key}:`);
      prefixes.add(`customSub:${ok}:`);
      prefixes.add(`customSub:${m.key}:`);
    }
    for (const s of m.sub || []) {
      const subHit =
        String(s.label || '')
          .toLowerCase()
          .includes(needle) || String(s.key).toLowerCase().includes(needle);
      if (subHit) {
        exact.add(`sub:${ok}:${s.key}`);
        exact.add(`sub:${m.key}:${s.key}`);
      }
    }
  }
  return { exact: [...exact], prefixes: [...prefixes] };
}

/**
 * SQL-фрагмент: заказ в разделе category (plain main или sub:main:…).
 * Возвращает { sql, params } где sql — выражение без AND, params — значения.
 * @param {string} category
 * @param {number} startIdx 1-based next $n
 */
function sqlOrderCategoryMatch(category, startIdx) {
  const cat = String(category || '').trim();
  if (!cat) return null;
  const main = getMainByKey(cat);
  const orderKey = main ? orderKeyForMain(main) : cat;
  const profileKey = main ? main.key : cat;
  const keys = Array.from(new Set([orderKey, profileKey].filter(Boolean)));
  const parts = [];
  const params = [];
  let idx = startIdx;
  for (const k of keys) {
    parts.push(`o.category = $${idx}`);
    params.push(k);
    idx += 1;
    parts.push(`o.category LIKE $${idx}`);
    params.push(`sub:${k}:%`);
    idx += 1;
  }
  return { sql: `(${parts.join(' OR ')})`, params, nextIdx: idx };
}

module.exports = {
  FREELANCE_MAIN_CATEGORIES,
  MAIN_ALIASES,
  getMainByKey,
  getSubLabel,
  orderKeyForMain,
  buildMainCategoryLabels,
  buildSubcategoryLabels,
  buildOrderCategoryOptions,
  buildAlertOrderCategoryOptions,
  canonicalizeOrderMain,
  categoryMatchersForSearch,
  sqlOrderCategoryMatch
};
